import { Assignee } from 'src/Utils/globalEnums'
import {
  DOCUMENT_REQUEST_STATUS,
  DocumentRequest,
  ListDocumentRequestsRequest,
  OwnerFilter,
  REQUEST_USE_CASE,
  RequestSort,
  StatusFilter,
} from '@trustero/trustero-api-web/lib/request/request_pb'
import queryString, { ParsedQuery } from 'query-string'
import {
  BoolValue,
  StringValue,
} from 'google-protobuf/google/protobuf/wrappers_pb'
import {
  GridColumnSortRequests,
  GridColumnSortType,
  SORT_ORDER,
} from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.constants'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { queryParamAsArray, queryParamAsString } from '../../Utils/queryParams'
import {
  DOCUMENT_REQUEST_LABEL_TO_STATUS,
  DocumentRequestStatusLabels,
  REQUEST_SORT_COLUMNS,
  REQUEST_ASSIGNEE_GROUP_PREFIX,
} from './requests.constants'

const APPROVAL_STATUSES: DOCUMENT_REQUEST_STATUS[] = [
  DOCUMENT_REQUEST_STATUS.OPEN,
  DOCUMENT_REQUEST_STATUS.APPROVED,
  DOCUMENT_REQUEST_STATUS.REJECTED,
]

// The statuses that answer a request (ntrced's RequestClosingStatuses).
const CLOSING_STATUSES: DOCUMENT_REQUEST_STATUS[] = [
  DOCUMENT_REQUEST_STATUS.COMPLETED,
  DOCUMENT_REQUEST_STATUS.APPROVED,
  DOCUMENT_REQUEST_STATUS.REJECTED,
]

// A request a vendor evaluation raised for one of its pauses -- its source
// names the run and the gate -- is final once answered: the evaluation has
// gone on from that answer, so its status can no longer change (ntrced
// refuses it). A request the agent raised as work names no gate.
export const isAnsweredPauseRequest = (request: DocumentRequest): boolean => {
  if (!CLOSING_STATUSES.includes(request.getStatus())) return false
  try {
    const source = JSON.parse(request.getSource())
    return (
      typeof source === 'object' &&
      source !== null &&
      Boolean(source.run_id) &&
      'gate' in source
    )
  } catch {
    return false
  }
}

// Approval requests are only ever Open/Approved/Rejected, and Approved/Rejected
// mean nothing on any other request type. The server enforces the same split.
export const getRequestStatusOptions = (
  useCaseType: number,
): DOCUMENT_REQUEST_STATUS[] =>
  useCaseType === REQUEST_USE_CASE.REQUEST_FOR_APPROVAL
    ? APPROVAL_STATUSES
    : (
        Object.values(DOCUMENT_REQUEST_STATUS) as DOCUMENT_REQUEST_STATUS[]
      ).filter(
        (status) =>
          status === DOCUMENT_REQUEST_STATUS.OPEN ||
          !APPROVAL_STATUSES.includes(status),
      )

export const applyRequestFilters = (
  req: ListDocumentRequestsRequest,
  queryParams: ParsedQuery<string>,
  filterType: FilterParam,
): void => {
  const filterParams = queryParamAsArray(queryParams[filterType])

  if (!filterParams?.length) {
    return
  }

  switch (filterType) {
    case FilterParam.STATUS: {
      const statuses = (filterParams as DocumentRequestStatusLabels[])?.map(
        (label) => DOCUMENT_REQUEST_LABEL_TO_STATUS[label],
      )
      const statusFilter = new StatusFilter().setStatusesList(statuses)
      req.setStatusFilter(statusFilter)
      break
    }
    case FilterParam.OWNER: {
      const ownerIds = new Set(filterParams)
      const ownerFilter = new OwnerFilter()
      if (ownerIds.has(Assignee.UNASSIGNED_BE)) {
        ownerIds.delete(Assignee.UNASSIGNED_BE)
        ownerFilter.setIncludeUnassigned(new BoolValue().setValue(true))
      }
      const selected = Array.from(ownerIds)
      ownerFilter.setOwnerIdsList(
        selected.filter(
          (value) => !value.startsWith(REQUEST_ASSIGNEE_GROUP_PREFIX),
        ),
      )
      ownerFilter.setOwnerGroupIdsList(
        selected
          .filter((value) => value.startsWith(REQUEST_ASSIGNEE_GROUP_PREFIX))
          .map((value) => value.slice(REQUEST_ASSIGNEE_GROUP_PREFIX.length))
          .filter(Boolean),
      )
      req.setOwnerFilter(ownerFilter)
      break
    }
    case FilterParam.REQUEST_USE_CASE_TYPE:
      // Multi-select — filtered client-side in RequestsIndexPage
      break
    case FilterParam.AUDIT: {
      req.setAuditId(new StringValue().setValue(filterParams[0]))
      break
    }
    case FilterParam.CREATED_SOURCE: {
      req.setSourceFilter(new StringValue().setValue(filterParams[0]))
      break
    }
    case FilterParam.REQUEST_SOURCE: {
      req.setSourceFilter(new StringValue().setValue(filterParams[0]))
      break
    }
    default:
      break
  }
}

export const applyRequestSort = (
  req: ListDocumentRequestsRequest,
  queryParams: ParsedQuery<string>,
): void => {
  const sortOrder =
    (queryParamAsString(queryParams.sort_by) as GridColumnSortType) || undefined
  const sortCol =
    (queryParamAsString(queryParams.sort_col) as GridColumnSortRequests) ||
    undefined
  const shouldApplySort = sortOrder && sortCol
  if (!shouldApplySort) {
    return
  }
  const sort = new RequestSort()
  sort.setSortOrder(SORT_ORDER[sortOrder])
  sort.setSortColumn(REQUEST_SORT_COLUMNS[sortCol])
  req.setRequestSort(sort)
}

export const getDRLFilterRequest = (
  locSearch: string,
): ListDocumentRequestsRequest => {
  const req = new ListDocumentRequestsRequest()
  const queryParams: ParsedQuery<string> = queryString.parse(locSearch, {
    arrayFormat: 'bracket',
  })
  // Apply all filters for each parameter in URL
  Object.values(FilterParam).forEach((filterType: FilterParam) =>
    applyRequestFilters(req, queryParams, filterType),
  )
  applyRequestSort(req, queryParams)
  return req
}
